import React, { PureComponent } from 'react'
import Icon from '@mdi/react'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import {
  mdiDockBottom,
  mdiDockLeft,
  mdiDockRight,
  mdiCursorMove,
  mdiClose,
  mdiDrag,
} from '@mdi/js'
import {
  DSA_HUD_PLAY_OPACITY,
  resolveCineDropLayout,
  normalizeDsaHudModality,
} from './DsaCineHud'
import {
  clearSnapGuide,
  defaultFreeCinePos,
  probeCineBesideDsa,
  queryDsaRect,
  rectFromBox,
  updateSnapGuide,
} from '../utils/panelEdgeSnap'

const STORAGE_KEY = 'udv-cine-panel-layout-by-mod'
const STORAGE_KEY_LEGACY = 'udv-cine-panel-layout'

const iconColor = '#FFFFFF'
const activeColor = '#81C784'
const muted = 'rgba(255,255,255,0.7)'

/** @typedef {'bottom'|'left'|'right'|'free'} CinePanelDock */

function defaultLayout() {
  return defaultFreeCinePos()
}

function normalizeLayout(parsed) {
  if (!parsed || typeof parsed !== 'object') return defaultLayout()
  // bottom/left/right legacy → free en coords por defecto o las guardadas
  const base = defaultFreeCinePos()
  const dock = parsed.dock === 'free' ? 'free' : parsed.dock === 'bottom' ? 'bottom' : 'free'
  return {
    dock: dock === 'bottom' ? 'bottom' : 'free',
    x: Number.isFinite(Number(parsed.x)) ? Number(parsed.x) : base.x,
    y: Number.isFinite(Number(parsed.y)) ? Number(parsed.y) : base.y,
  }
}

/**
 * @param {string} [modality]
 */
export function loadCinePanelLayout(modality) {
  const key = normalizeDsaHudModality(modality)
  try {
    const rawMap = localStorage.getItem(STORAGE_KEY)
    if (rawMap) {
      const map = JSON.parse(rawMap)
      if (map && map[key]) return normalizeLayout(map[key])
      if (map && map.DEFAULT) return normalizeLayout(map.DEFAULT)
    }
    const legacy = localStorage.getItem(STORAGE_KEY_LEGACY)
    if (legacy) {
      const layout = normalizeLayout(JSON.parse(legacy))
      saveCinePanelLayout(layout, 'DEFAULT')
      return layout
    }
  } catch (e) {
    /* ignore */
  }
  return defaultLayout()
}

/**
 * @param {{ dock?: string, x?: number|null, y?: number|null }} layout
 * @param {string} [modality]
 */
export function saveCinePanelLayout(layout, modality) {
  const key = normalizeDsaHudModality(modality)
  const next = normalizeLayout(layout)
  try {
    let map = {}
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      try {
        map = JSON.parse(raw) || {}
      } catch (e) {
        map = {}
      }
    }
    map[key] = next
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch (e) {
    /* ignore */
  }
}

function TinyBtn({ title, path, onClick, active }) {
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label={title}
        style={{
          color: active ? activeColor : iconColor,
          padding: 3,
          background: active
            ? 'rgba(129,199,132,0.16)'
            : 'rgba(255,255,255,0.06)',
          margin: 1,
        }}
      >
        <Icon
          path={path}
          size="0.95rem"
          color={active ? activeColor : iconColor}
        />
      </IconButton>
    </Tooltip>
  )
}

/**
 * Contenedor del panel de cine: dock bottom/izq/der, libre + snap,
 * dim 30% en Play, persistencia por modalidad (vía onLayoutChange).
 */
class CinePanelShell extends PureComponent {
  _drag = null
  state = { hovered: false }

  setDock = (dock) => {
    // Desacoplar: conservar posición en pantalla de ambos paneles
    if (dock === 'free' && this.props.embedded && typeof this.props.onUncouple === 'function') {
      this.props.onUncouple()
      return
    }
    if (typeof this.props.onLayoutChange !== 'function') return
    if (dock === 'bottom') {
      const pos = defaultFreeCinePos()
      this.props.onLayoutChange({ dock: 'free', x: pos.x, y: pos.y })
      return
    }
    if (dock === 'free') {
      const rect = this._rootEl && this._rootEl.getBoundingClientRect()
      const next = rect
        ? { dock: 'free', x: Math.round(rect.left), y: Math.round(rect.top) }
        : defaultFreeCinePos()
      this.props.onLayoutChange(next)
      return
    }
    // left/right en cine: atajo de posición free
    const pos = defaultFreeCinePos()
    const vw = window.innerWidth || 360
    if (dock === 'left') {
      this.props.onLayoutChange({ dock: 'free', x: 8, y: pos.y })
    } else {
      this.props.onLayoutChange({
        dock: 'free',
        x: Math.max(8, vw - 320),
        y: pos.y,
      })
    }
  }

  uncouple = (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (typeof this.props.onUncouple === 'function') {
      this.props.onUncouple()
      return
    }
    this.setDock('free')
  }

  onDragHandlePointerDown = (e) => {
    if (this.props.linkedDragHandlers && this.props.linkedDragHandlers.onPointerDown) {
      this.props.linkedDragHandlers.onPointerDown(e)
      return
    }
    const layout = this.props.layout || {}
    let originX
    let originY
    const rect = this._rootEl && this._rootEl.getBoundingClientRect()
    if (layout.dock !== 'free') {
      originX = rect ? rect.left : e.clientX
      originY = rect ? rect.top : e.clientY
      if (typeof this.props.onLayoutChange === 'function') {
        this.props.onLayoutChange({
          dock: 'free',
          x: Math.round(originX),
          y: Math.round(originY),
        })
      }
      if (this._rootEl) {
        Object.assign(this._rootEl.style, {
          position: 'fixed',
          left: `${Math.round(originX)}px`,
          top: `${Math.round(originY)}px`,
          bottom: 'auto',
          right: 'auto',
          margin: '0',
          zIndex: '140000',
          transform: 'none',
        })
      }
    } else {
      originX = Number.isFinite(Number(layout.x))
        ? Number(layout.x)
        : rect
          ? rect.left
          : e.clientX
      originY = Number.isFinite(Number(layout.y))
        ? Number(layout.y)
        : rect
          ? rect.top
          : e.clientY
    }

    this._drag = {
      startX: e.clientX,
      startY: e.clientY,
      originX,
      originY,
    }
    this._snapPreview = null
    this._pendingPos = { x: Math.round(originX), y: Math.round(originY) }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (err) {
      /* ignore */
    }
    e.preventDefault()
    e.stopPropagation()
  }

  onDragHandlePointerMove = (e) => {
    if (this.props.linkedDragHandlers && this.props.linkedDragHandlers.onPointerMove) {
      this.props.linkedDragHandlers.onPointerMove(e)
      return
    }
    if (!this._drag) return
    const dx = e.clientX - this._drag.startX
    const dy = e.clientY - this._drag.startY
    const w = this._rootEl ? this._rootEl.offsetWidth : 300
    const h = this._rootEl ? this._rootEl.offsetHeight : 140
    // Limitar Y durante el drag para no meter el panel en la barra inferior
    const vw = window.innerWidth || 360
    const vh = window.innerHeight || 640
    let cineBottom = 56
    try {
      const css = getComputedStyle(document.documentElement).getPropertyValue(
        '--udv-cine-bottom'
      )
      const n = parseFloat(css)
      if (Number.isFinite(n)) cineBottom = n
    } catch (err) {
      /* ignore */
    }
    let x = Math.max(4, Math.min(vw - w - 4, this._drag.originX + dx))
    const maxY = Math.max(4, vh - cineBottom - h - 8)
    let y = Math.max(4, Math.min(maxY, this._drag.originY + dy))

    const linked = document.querySelector('[data-linked-panels="true"]')
    this._snapPreview = null
    if (!linked) {
      const dsa = queryDsaRect()
      if (dsa) {
        const hit = probeCineBesideDsa(rectFromBox(x, y, w, h), dsa)
        if (hit) {
          this._snapPreview = hit
          x = hit.x
          y = hit.y
          updateSnapGuide(hit.guide)
        } else {
          clearSnapGuide()
        }
      } else {
        clearSnapGuide()
      }
    } else {
      clearSnapGuide()
    }

    this._pendingPos = { x: Math.round(x), y: Math.round(y) }
    if (this._rootEl) {
      this._rootEl.style.left = `${this._pendingPos.x}px`
      this._rootEl.style.top = `${this._pendingPos.y}px`
      this._rootEl.style.boxShadow = this._snapPreview
        ? '0 0 0 1px rgba(129,199,132,0.7), 0 2px 14px rgba(0,0,0,0.4)'
        : '0 2px 14px rgba(0,0,0,0.4)'
    }
    e.stopPropagation()
  }

  onDragHandlePointerUp = (e) => {
    if (this.props.linkedDragHandlers && this.props.linkedDragHandlers.onPointerUp) {
      this.props.linkedDragHandlers.onPointerUp(e)
      return
    }
    if (!this._drag) return
    const drag = this._drag
    this._drag = null
    const snapPreview = this._snapPreview
    this._snapPreview = null
    clearSnapGuide()
    if (this._pendingPos && typeof this.props.onLayoutChange === 'function') {
      const rect = this._rootEl && this._rootEl.getBoundingClientRect()
      const w = rect && rect.width > 40 ? rect.width : 300
      const h = rect && rect.height > 40 ? Math.min(rect.height, 240) : 140
      const moved =
        Math.abs(this._pendingPos.x - drag.originX) > 4 ||
        Math.abs(this._pendingPos.y - drag.originY) > 4
      if (!moved) {
        this.props.onLayoutChange({
          dock: 'free',
          x: this._pendingPos.x,
          y: this._pendingPos.y,
        })
      } else {
        const drop = resolveCineDropLayout(
          this._pendingPos.x,
          this._pendingPos.y,
          w,
          h,
          snapPreview
        )
        if (this._rootEl) {
          this._rootEl.style.boxShadow = '0 2px 14px rgba(0,0,0,0.4)'
          if (drop.x != null) {
            this._rootEl.style.left = `${drop.x}px`
            this._rootEl.style.top = `${drop.y}px`
          }
        }
        this.props.onLayoutChange({
          dock: 'free',
          x: drop.x,
          y: drop.y,
          dsaSide: drop.dsaSide,
          groupMode: 'free',
          coupled: drop.coupled,
        })
      }
    }
    this._pendingPos = null
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch (err) {
      /* ignore */
    }
    e.stopPropagation()
  }

  setRootRef = (el) => {
    this._rootEl = el
  }

  onHoverEnter = () => this.setState({ hovered: true })
  onHoverLeave = () => this.setState({ hovered: false })

  render() {
    const {
      layout = { dock: 'bottom' },
      inPlay = false,
      children,
      frameLabel,
      stopPointer,
      embedded = false,
    } = this.props

    const dock = embedded ? 'bottom' : layout.dock || 'bottom'
    const dimmed = !embedded && !!inPlay && !this.state.hovered && !this._drag

    const shellStyle = {
      width: 'min(92vw, 300px)',
      flex: '0 1 auto',
      flexShrink: 0,
      backgroundColor: 'rgba(40, 40, 48, 0.88)',
      borderRadius: 8,
      pointerEvents: 'auto',
      touchAction: 'manipulation',
      WebkitUserSelect: 'none',
      userSelect: 'none',
      boxShadow: '0 2px 14px rgba(0,0,0,0.4)',
      position: 'relative',
      zIndex: 51,
      opacity: dimmed ? DSA_HUD_PLAY_OPACITY : 1,
      transition: 'opacity 0.2s ease',
      margin: 0,
    }

    if (!embedded && dock === 'free') {
      shellStyle.position = 'fixed'
      shellStyle.left = Number.isFinite(Number(layout.x))
        ? Number(layout.x)
        : Math.max(8, Math.round((window.innerWidth || 360) / 2 - 150))
      shellStyle.top = Number.isFinite(Number(layout.y))
        ? Number(layout.y)
        : Math.max(8, (window.innerHeight || 640) - 200)
      shellStyle.bottom = 'auto'
      shellStyle.right = 'auto'
      shellStyle.zIndex = 140000
    } else if (!embedded && (dock === 'left' || dock === 'right')) {
      shellStyle.position = 'fixed'
      shellStyle.bottom = 'var(--udv-cine-bottom, 56px)'
      shellStyle.top = 'auto'
      shellStyle.zIndex = 140000
      if (dock === 'left') {
        shellStyle.left = 8
        shellStyle.right = 'auto'
      } else {
        shellStyle.right = 8
        shellStyle.left = 'auto'
      }
    }

    const stop = stopPointer || ((e) => e.stopPropagation())

    return (
      <div
        ref={this.setRootRef}
        data-cine-controls="true"
        data-cine-dock={dock}
        data-cine-dimmed={dimmed ? '1' : '0'}
        onPointerDown={stop}
        onPointerUp={stop}
        onPointerMove={stop}
        onTouchStart={stop}
        onTouchEnd={stop}
        onTouchMove={stop}
        onMouseDown={stop}
        onMouseUp={stop}
        onClick={stop}
        onMouseEnter={this.onHoverEnter}
        onMouseLeave={this.onHoverLeave}
        onPointerEnter={this.onHoverEnter}
        onPointerLeave={this.onHoverLeave}
        style={shellStyle}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            padding: '4px 6px 2px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <div
            onPointerDown={this.onDragHandlePointerDown}
            onPointerMove={this.onDragHandlePointerMove}
            onPointerUp={this.onDragHandlePointerUp}
            onPointerCancel={this.onDragHandlePointerUp}
            title={
              this.props.linkedDragHandlers
                ? 'Mover cine + DSA juntos'
                : 'Arrastrar libre · acerca el borde al DSA para acoplar (guía verde)'
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              cursor: 'grab',
              flex: 1,
              minWidth: 0,
              touchAction: 'none',
            }}
          >
            <Icon path={mdiDrag} size="1rem" color={muted} />
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: activeColor,
              }}
            >
              CINE{inPlay ? ' · ▶' : ''}
            </span>
          </div>
          <div style={{ display: 'flex', flexShrink: 0 }}>
            {embedded ? (
              <TinyBtn
                title="Desacoplar · conserva posición y arrastra libre"
                path={mdiClose}
                active={false}
                onClick={this.uncouple}
              />
            ) : (
              <>
                <TinyBtn
                  title="Posición inferior"
                  path={mdiDockBottom}
                  active={false}
                  onClick={() => this.setDock('bottom')}
                />
                <TinyBtn
                  title="Posición izquierda"
                  path={mdiDockLeft}
                  active={false}
                  onClick={() => this.setDock('left')}
                />
                <TinyBtn
                  title="Posición derecha"
                  path={mdiDockRight}
                  active={false}
                  onClick={() => this.setDock('right')}
                />
                <TinyBtn
                  title="Libre · acerca bordes al DSA para acoplar"
                  path={mdiCursorMove}
                  active={dock === 'free'}
                  onClick={() => this.setDock('free')}
                />
              </>
            )}
          </div>
        </div>
        {children}
        {frameLabel}
      </div>
    )
  }
}

export default CinePanelShell
